import { FeedbackMessage } from '../entry-components/ai-smart-search/ai-smart-search-feedback-area.model';
import { AIMessagePayload, AIResponseType, AiCourse } from '../entry-components/ai-smart-search/ai-smart-search.model';
import { DataService } from '../services/data_service';
import { EntryComponentConfig } from '../shared/types/common.model';

/**
 * Backend course response structure
 */
export interface AISearchCourseDto {
  course_id: number;
  course_guid: string;
  course_name: string;
  description: string;
  has_badges: boolean;
  has_certificates: boolean;
  difficulty_level: number;
  minute_length: number;
  image_url: string;
  image_id: string | number;
}

/**
 * Enum representing the possible response types from the AI Smart Search client.
 *
 * @remarks
 * - `SUCCESS`: Indicates that the search returned successful results.
 * - `FAILURE`: Indicates that the input provided was insufficient for a successful search.
 */
export enum ResponseType {
  SUCCESS = 'successfull_results',
  FAILURE = 'in_sufficient_input',
}

/**
 * Backend success response structure
 *
 * introduction - Message from the AI introducing the results
 * followup - Message from the AI suggesting next steps or follow-up
 * courses - List of courses returned by the AI, keyed by course_id
 */
export interface AISearchSuccessResponseDto {
  introduction: string;
  followup: string;
  courses: Record<string, AISearchCourseDto>;
}

/**
 * Backend failure response structure
 */
export interface AISearchFailureResponseDto {
  reason_to_fail: string;
}

/**
 * API response interface matching the Golang backend structure
 */
export interface AIApiResponse {
  chat_guid: string;
  type: AIResponseType;
  response: AISearchSuccessResponseDto | AISearchFailureResponseDto;
  debug?: any;
  performance_metrics?: Record<string, number>;
}

/**
 * Transformed response format that matches the frontend Course interface
 */
export interface AISearchProcessedResponse {
  chat_guid: string;
  type: AIResponseType;
  response: {
    introduction?: string;
    courses?: AiCourse[];
    followup?: string;
    reason_to_fail?: string;
  };
  debug?: any;
  performance_metrics?: Record<string, number>;
}

/**
 * AI Smart Search API Client
 * Handles communication with the AI Smart Search backend service
 */
export class AiSmartSearchClient {
  private dataService: DataService;
  private aiApiEndpoint: string;
  private aiApiTimeout: number;

  constructor(internalConfig: EntryComponentConfig) {
    this.dataService = DataService.getInstance(internalConfig.baseUrl);

    // Use AI-specific endpoint if provided, otherwise fallback to base URL + default path
    this.aiApiEndpoint = internalConfig.aiApiEndpoint || `${internalConfig.baseUrl}/ai-assistant`;
    this.aiApiTimeout = internalConfig.aiApiTimeout || 30000;
  }

  /**
   * Send a message to the AI Smart Search API
   * @param payload - Message payload containing user message and optional chat_guid
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Promise with transformed API response
   */
  async sendMessage(payload: AIMessagePayload, signal?: AbortSignal): Promise<AISearchProcessedResponse> {
    const path = '/chat'; // Relative to aiApiEndpoint

    try {
      // Create timeout signal if not provided
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => timeoutController.abort(), this.aiApiTimeout);

      // Use the provided signal or timeout signal
      const effectiveSignal = signal || timeoutController.signal;

      const rawResponse = await this.dataService.post<{ data: AIApiResponse }>(
        this.aiApiEndpoint + path,
        {
          message: payload.message,
          chat_guid: payload.chat_guid || undefined,
        },
        { signal: effectiveSignal }
      );

      clearTimeout(timeoutId);

      // Extract the actual response from the data wrapper
      const response = rawResponse.data;

      // Transform the response to match frontend interfaces
      return this.transformResponse(response);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('AI request timed out');
      }
      throw new Error(`AI API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * * Sends user feedback about AI search results to the backend
   *
   * @param payload - The feedback message containing user's feedback, category selection, and reference information
   * @returns Promise with the server's response to the feedback submission
   */
  async sendFeedback(payload: FeedbackMessage): Promise<FeedbackMessage> {
    const path = '/feedback'; // Relative to aiApiEndpoint

    try {
      const rawResponse = await this.dataService.post<{ data: FeedbackMessage }>(this.aiApiEndpoint + path, {
        chat_guid: payload.chat_guid,
        feedback: payload.feedback,
        category: payload.category,
        is_positive: payload.is_positive,
      });

      return rawResponse.data;
    } catch (error) {
      throw new Error(`AI API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Transform backend response format to frontend format
   * Maps backend course structure to frontend Course interface
   */
  private transformResponse(response: AIApiResponse): AISearchProcessedResponse {
    let transformedResponse: {
      introduction?: string;
      courses?: AiCourse[];
      followup?: string;
      reason_to_fail?: string;
    };

    if (response.type === ResponseType.SUCCESS) {
      const successResponse = response.response as AISearchSuccessResponseDto;
      const transformedCourses = Object.values(successResponse.courses || {}).map((course) => ({
        course_id: course.course_id,
        name: course.course_name,
        description: course.description,
        number_of_rates: undefined as number | undefined, // Missing from core cache currently
        star_sum: undefined as number | undefined, // Missing from core cache currently
        image_url: course.image_url || undefined,
        course_image_id: course.image_id || undefined,
      }));

      transformedResponse = {
        introduction: successResponse.introduction,
        courses: transformedCourses,
        followup: successResponse.followup,
      };
    } else {
      const failureResponse = response.response as AISearchFailureResponseDto;
      transformedResponse = {
        reason_to_fail: failureResponse.reason_to_fail,
      };
    }

    return {
      chat_guid: response.chat_guid,
      type: response.type,
      response: transformedResponse,
      debug: response.debug,
      performance_metrics: response.performance_metrics,
    };
  }

  /**
   * Get the message to display to the user based on response type
   * @param response - The transformed API response
   * @returns The appropriate message to display
   */
  getDisplayMessage(response: AISearchProcessedResponse): string | undefined {
    if (response.type === ResponseType.SUCCESS) {
      return response.response.introduction;
    } else {
      return response.response.reason_to_fail;
    }
  }

  /**
   * Check if the response indicates a successful search with courses
   * @param response - The transformed API response
   * @returns True if response contains courses
   */
  hasSuccessfulResults(response: AISearchProcessedResponse): boolean {
    return (
      response.type === ResponseType.SUCCESS &&
      Array.isArray(response.response.courses) &&
      response.response.courses.length > 0
    );
  }

  /**
   * Check if the response is an error or non-success response
   * @param response - The transformed API response
   * @returns True if response indicates an error or non-success state
   */
  isErrorResponse(response: AISearchProcessedResponse): boolean {
    return response.type !== ResponseType.SUCCESS;
  }

  /**
   * Type guard to check if response is a success response
   * @param response - The API response
   * @returns True if response is a success response
   */
  private isSuccessResponse(
    response: AIApiResponse
  ): response is AIApiResponse & { response: AISearchSuccessResponseDto } {
    return response.type === ResponseType.SUCCESS;
  }

  /**
   * Type guard to check if response is a failure response
   * @param response - The API response
   * @returns True if response is a failure response
   */
  private isFailureResponse(
    response: AIApiResponse
  ): response is AIApiResponse & { response: AISearchFailureResponseDto } {
    return response.type === ResponseType.FAILURE;
  }

  /**
   * Get the followup message from a successful response
   * @param response - The transformed AI response
   * @returns The followup message if available
   */
  getFollowupMessage(response: AISearchProcessedResponse): string | undefined {
    if (this.hasSuccessfulResults(response)) {
      return response.response.followup;
    }
    return undefined;
  }

  /**
   * Get the reason for failure from an error response
   * @param response - The transformed AI response
   * @returns The reason for failure if available
   */
  getFailureReason(response: AISearchProcessedResponse): string | undefined {
    if (this.isErrorResponse(response)) {
      return response.response.reason_to_fail;
    }
    return undefined;
  }
}
